import { type Editor } from '@tiptap/react';
import * as React from 'react';

// --- Hooks ---
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';

// --- Icons ---
import { ChevronDownIcon } from '@/components/tiptap-editor/tiptap-icons/chevron-down-icon';
import { ZyCodeIcon } from '@/components/tiptap-editor/tiptap-icons/zycode-icon';
import {
    ArrowRight,
    Blocks,
    ChevronsLeftRightEllipsis,
    Film,
    Hammer,
    LayoutDashboard,
    Pencil,
    SquareDashedBottomCode,
    ToolCase,
} from 'lucide-react';

// --- Lib ---
import { isNodeInSchema } from '@/lib/tiptap-utils';
import { isSelectionTopNode } from '@/lib/zy-tiptap-utils';

// --- UI Primitives ---
import type { ButtonProps } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Card, CardBody } from '@/components/tiptap-editor/tiptap-ui-primitive/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/tiptap-editor/tiptap-ui-primitive/dropdown-menu';

export interface ZyToolOption {
    label: string;
    extensionName: string;
    icon: React.ComponentType<{ className?: string }>;
    command: (editor: Editor) => void;
}

export const zyToolOptions: ZyToolOption[] = [
    {
        label: 'Animation',
        extensionName: 'zyAnimator',
        icon: Film,
        command: (editor: Editor) => editor.commands.insertZyAnimator(),
    },
    {
        label: 'Progression',
        extensionName: 'progressionBuilder',
        icon: ArrowRight,
        command: (editor: Editor) => editor.commands.insertProgressionBuilder(),
    },
    {
        label: 'Block Ordering',
        extensionName: 'blockOrderingBuilder',
        icon: Blocks,
        command: (editor: Editor) => editor.commands.insertBlockOrderingBuilder(),
    },
    {
        label: 'Code Writing',
        extensionName: 'codeWritingBuilder',
        icon: Pencil,
        command: (editor: Editor) => editor.commands.insertCodeWritingBuilder(),
    },
    {
        label: 'Code Reading',
        extensionName: 'codeOutputBuilder',
        icon: ChevronsLeftRightEllipsis,
        command: (editor: Editor) => editor.commands.insertCodeOutputBuilder(),
    },
    {
        label: 'Parsons Coding',
        extensionName: 'parsonsCodingBuilder',
        icon: ZyCodeIcon,
        command: (editor: Editor) => editor.commands.insertParsonsCodingBuilder(),
    },
    {
        label: 'OpenStax Python',
        extensionName: 'openStaxPythonCodeRunnerBuilder',
        icon: SquareDashedBottomCode,
        command: (editor: Editor) => editor.commands.insertOpenStaxPythonCodeRunnerBuilder(),
    },
    {
        label: 'Streamlit app',
        extensionName: 'streamlitApp',
        icon: LayoutDashboard,
        command: (editor: Editor) => editor.commands.insertStreamlitApp(),
    },
    {
        label: 'zyTool',
        extensionName: 'zyTool',
        icon: Hammer,
        command: (editor: Editor) => editor.commands.insertZyTool(),
    },
    {
        label: 'MathWorks Learning Tool',
        extensionName: 'zyMathWorksLearningTool',
        icon: Hammer,
        command: (editor: Editor) => editor.commands.insertZyMathWorksLearningTool(),
    },
];

const atLeastOneZyToolOptionCanBeInserted = (editor: Editor | null) => {
    if (!editor) {
        return false;
    }

    return zyToolOptions.some(option => isNodeInSchema(option.extensionName, editor));
};

export interface ZyToolMenuProps extends Omit<ButtonProps, 'type'> {
    /**
     * The TipTap editor instance.
     */
    editor?: Editor;

    onOpenChange?: (isOpen: boolean) => void;
}

export function useZyToolMenuState(editor: Editor | null) {
    const [isOpen, setIsOpen] = React.useState(false);

    const canInsert = editor && isSelectionTopNode(editor);

    const handleOpenChange = React.useCallback((open: boolean, callback?: (isOpen: boolean) => void) => {
        setIsOpen(open);
        callback?.(open);
    }, []);

    return {
        isOpen,
        setIsOpen,
        canInsert,
        handleOpenChange,
    };
}

export function ZyToolDropdownMenu({ editor: providedEditor, onOpenChange, ...props }: ZyToolMenuProps) {
    const { editor } = useTiptapEditor(providedEditor);

    const { isOpen, canInsert, handleOpenChange } = useZyToolMenuState(editor);

    const disabled = !canInsert;

    const show = !!editor;
    const handleOnOpenChange = React.useCallback((open: boolean) => handleOpenChange(open, onOpenChange), [handleOpenChange, onOpenChange]);

    const handleZyToolClick = React.useCallback(
        (zyTool: ZyToolOption) => {
            if (!editor) return;
            zyTool.command(editor);
            handleOpenChange(false, onOpenChange);
        },
        [editor, handleOpenChange, onOpenChange],
    );

    if (!show || !editor || !editor.isEditable) {
        return null;
    }

    return atLeastOneZyToolOptionCanBeInserted(editor) ? (
        <DropdownMenu
            open={isOpen}
            onOpenChange={handleOnOpenChange}
        >
            <DropdownMenuTrigger asChild>
                <Button
                    type="button"
                    data-style="ghost"
                    data-active-state="off"
                    role="button"
                    tabIndex={-1}
                    aria-label="Insert zyTool"
                    tooltip="zyTool"
                    {...props}
                >
                    <ToolCase className="tiptap-button-icon" />
                    <ChevronDownIcon className="tiptap-button-dropdown-small" />
                </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent>
                <Card>
                    <CardBody>
                        <DropdownMenuGroup>
                            {zyToolOptions.map(option => {
                                return isNodeInSchema(option.extensionName, editor) ? (
                                    <DropdownMenuItem
                                        key={option.label}
                                        asChild
                                        disabled={disabled}
                                    >
                                        <Button
                                            type="button"
                                            data-style="ghost"
                                            role="button"
                                            tabIndex={-1}
                                            disabled={disabled}
                                            onClick={() => handleZyToolClick(option)}
                                        >
                                            <div className="flex-start flex w-full">
                                                <option.icon className="mr-2 h-4 w-4" />
                                                <span>{option.label}</span>
                                            </div>
                                        </Button>
                                    </DropdownMenuItem>
                                ) : null;
                            })}
                        </DropdownMenuGroup>
                    </CardBody>
                </Card>
            </DropdownMenuContent>
        </DropdownMenu>
    ) : null;
}

export default ZyToolDropdownMenu;
